import { assertNever, isDefined } from '../variables';
import { getViteEnv } from './vite-env';

export type EnvironmentVariableName =
  | 'NODE_ENV'
  | 'DOMAIN'
  | 'INTERNAL_API_DOMAIN'
  | 'PUBLIC'
  | 'WEBSOCKET_URL'
  | 'XSITE_INTEGRATION_DOMAIN'
  | 'LEICA_INTEGRATION_DOMAIN'
  | 'CERBOS_URL'
  | 'AZURE_MAPS_CLIENT_ID'
  | 'ZITADEL_ISSUER'
  | 'ZITADEL_CLIENT_ID'
  | 'ZITADEL_PROJECT_ID';

/**
 * Retrieves the value of an environment variable by its name.
 * @param {EnvironmentVariableName} variableName - The name of the environment variable to retrieve.
 * @param {string} [customErrorMessage] - Optional custom error message to throw if the environment variable is not found.
 * @throws Will throw an error if the environment variable is not set.
 * @returns {string} The value of the environment variable as a string.
 */
export const assertEnvVariable = (
  variableName: EnvironmentVariableName,
  customErrorMessage?: string,
): string => {
  const value = resolveEnvironmentVariable(variableName);
  if (!isDefined(value)) {
    throw new Error(customErrorMessage || `Environment variable ${variableName} is not set.`);
  }
  return value;
};

// In Vite builds, env vars are exposed via import.meta.env (prefixed with VITE_).
// Access via dynamic evaluation so TS doesn't require ESM or vite/client types in
// non-Vite contexts (e.g. tests and tooling).
function getViteEnvVar(key: string): string | undefined {
  const viteKey = key.startsWith('VITE_') ? key : `VITE_${key}`;
  return getViteEnv(viteKey);
}

/**
 *  Vite statically replaces import.meta.env accesses with the actual values at build time, which
 *  makes dynamically extracting the values moot. This maps the environment variables to their
 *  replaced values.
 */
const resolveEnvironmentVariable = (variableName: EnvironmentVariableName) => {
  switch (variableName) {
    case 'NODE_ENV':
      return getViteEnvVar('VITE_NODE_ENV') || process.env.NODE_ENV;
    case 'DOMAIN':
      return getViteEnvVar('VITE_DOMAIN') || process.env.DOMAIN;
    case 'INTERNAL_API_DOMAIN':
      return getViteEnvVar('VITE_INTERNAL_API_DOMAIN') || process.env.INTERNAL_API_DOMAIN;
    case 'PUBLIC':
      return getViteEnvVar('VITE_PUBLIC') || process.env.PUBLIC;
    case 'WEBSOCKET_URL':
      return getViteEnvVar('VITE_WEBSOCKET_URL') || process.env.WEBSOCKET_URL;
    case 'XSITE_INTEGRATION_DOMAIN':
      return getViteEnvVar('VITE_XSITE_INTEGRATION_DOMAIN') || process.env.XSITE_INTEGRATION_DOMAIN;
    case 'LEICA_INTEGRATION_DOMAIN':
      return getViteEnvVar('VITE_LEICA_INTEGRATION_DOMAIN') || process.env.LEICA_INTEGRATION_DOMAIN;
    case 'CERBOS_URL':
      return getViteEnvVar('VITE_CERBOS_URL') || process.env.CERBOS_URL;
    case 'AZURE_MAPS_CLIENT_ID':
      return getViteEnvVar('VITE_AZURE_MAPS_CLIENT_ID') || process.env.AZURE_MAPS_CLIENT_ID;
    case 'ZITADEL_ISSUER':
      return getViteEnvVar('VITE_ZITADEL_ISSUER') || process.env.ZITADEL_ISSUER;
    case 'ZITADEL_CLIENT_ID':
      return getViteEnvVar('VITE_ZITADEL_CLIENT_ID') || process.env.ZITADEL_CLIENT_ID;
    case 'ZITADEL_PROJECT_ID':
      return getViteEnvVar('VITE_ZITADEL_PROJECT_ID') || process.env.ZITADEL_PROJECT_ID;
    default:
      assertNever(variableName);
  }
};
